import useSitecoreStore from '@/stores/sitecore';
import type { AppContext } from '@/types';
import type { SubState } from '@shared-jss/types';
import type { Nullable } from 'design.zorgdomein/dist/typings/types/generics';
import type { UnwrapRef } from 'vue';
import { reactive } from 'vue';

type Data<T> = Nullable<T>;

const useSubState = <T>(
    getFn: (
        appContext: AppContext,
        language: string,
        pageEditing: boolean
    ) => Promise<UnwrapRef<Data<T>>>,
    initialState: Partial<SubState<T>> = {
        data: null,
        loading: false,
        error: false,
        changed: false
    }
): [
    {
        data: Nullable<UnwrapRef<T>>;
        loading: boolean;
        error: boolean;
        changed?: boolean;
    },
    (force?: boolean) => Promise<void>,
    () => void
] => {
    const sitecoreStore = useSitecoreStore();
    const state = reactive<SubState<T>>({
        data: null,
        loading: false,
        error: false,
        changed: false,
        ...initialState
    });

    const get = async (force?: boolean) => {
        if (!force && state.data) return;

        state.loading = true;
        state.error = false;

        const response = await getFn(
            sitecoreStore.appContext,
            sitecoreStore.language,
            sitecoreStore.pageEditing
        );

        if (response) {
            state.changed = JSON.stringify(response) !== JSON.stringify(state.data);
            state.data = response;
        } else {
            state.error = true;
        }

        state.loading = false;
    };

    const clear = () => {
        state.data = null;
        state.loading = false;
        state.error = false;
        state.changed = false;
    };

    return [state, get, clear];
};

export default useSubState;
